import { Loader } from "semantic-ui-react";

import { useCan, useSelectedDealer } from "components";
import { createFormHelper } from "lib/Form";
import { SMS_GATEWAYS } from "models";
import { DealerOverviewHeader, DmsSystem, General, Service } from "modules/DealerOverviewDashboard/components";
import { isThresholdInvalid } from "modules/DealerOverviewDashboard/components/DmsSystem/components";
import { useListThresholdsQuery, useUpdateThresholdsMutation } from "modules/DealerOverviewDashboard/components/DmsSystem/components/ThresholdSettings/hooks";
import "modules/DealerOverviewDashboard/DealerOverviewDashboard.scss";
import { useUpdateDealerMutation } from "modules/DealerOverviewDashboard/hooks";

import type { Dealer } from "models";
import type { DefaultReportThreshold } from "modules/DealerOverviewDashboard/components/DmsSystem/components/ThresholdSettings/hooks";
import type { DealerOverviewFormPayload } from "modules/DealerOverviewDashboard/hooks";

const FormHelper = createFormHelper<DealerOverviewFormPayload>();

const getDefaultFormValues = (dealer: Dealer, dealerReportThresholds: DefaultReportThreshold[]): DealerOverviewFormPayload => ({
  name: dealer.name,
  city: dealer.city,
  street: dealer.street,
  postal_code: dealer.postal_code,
  phone_nr: dealer.phone_nr,
  website_url: dealer.website_url,
  region: dealer.region,
  country_code: dealer.country_code,
  sys_admin_name: dealer.sys_admin_name,
  sys_admin_email: dealer.sys_admin_email,
  sys_admin_phone: dealer.sys_admin_phone,
  dms_server_info: dealer.dms_server_info,
  network_info: dealer.network_info,
  domain_name: dealer.domain?.name ?? dealer.domain_name,
  is_snooze_type_next_visit_customer_enabled: dealer.is_snooze_type_next_visit_customer_enabled,
  is_snooze_type_remind_customer_enabled: dealer.is_snooze_type_remind_customer_enabled,
  is_snooze_type_make_new_appointment_enabled: dealer.is_snooze_type_make_new_appointment_enabled,
  is_snooze_type_make_offer_enabled: dealer.is_snooze_type_make_offer_enabled,
  is_snooze_status_enabled: dealer.is_snooze_status_enabled,
  is_snooze_department_enabled: dealer.is_snooze_department_enabled,
  is_snooze_attachment_enabled: dealer.is_snooze_attachment_enabled,
  enabled_warranty_types: dealer.enabled_warranty_types ?? [],
  web_clock_url: dealer.web_clock_url,
  is_xentry_enabled: dealer.is_xentry_enabled,
  sms_gateway_id: dealer.sms_gateway_id,
  twilio_account_sid: dealer.twilio_account_sid,
  twilio_api_key: dealer.twilio_api_key,
  twilio_phone_number: dealer.twilio_phone_number,
  twilio_monthly_dollar_limit: dealer.twilio_monthly_dollar_limit,
  twilio_monthly_sms_limit: dealer.twilio_monthly_sms_limit,
  planit_planning_enabled: dealer.planit_planning_enabled,
  planit_planning_user_name: dealer.planit_planning_user_name,
  onei_planning_enabled: dealer.onei_planning_enabled,
  onei_planning_environment_guid: dealer.onei_planning_environment_guid,
  dealer_report_thresholds: dealerReportThresholds
});

const clearDisabledServiceSecrets = (values: DealerOverviewFormPayload): DealerOverviewFormPayload => ({
  ...values,
  ...(!values.planit_planning_enabled && { planit_planning_user_name: "", planit_planning_password: undefined }),
  ...(!values.onei_planning_enabled && { onei_planning_environment_guid: "", onei_planning_api_key: undefined }),
  ...(!values.is_xentry_enabled && { xentry_client_id: undefined, xentry_client_secret: undefined }),
  ...(values.sms_gateway_id !== SMS_GATEWAYS.Twilio && { twilio_auth_token: undefined, twilio_api_secret: undefined })
});

const isThresholdEntered = ({ low_value, high_value }: DefaultReportThreshold) => low_value != null || high_value != null;

const isFormInvalid = (values: DealerOverviewFormPayload) => {
  const isWarrantyValid = values.enabled_warranty_types?.length;
  const isPlanitValid = !values.planit_planning_enabled || values.planit_planning_user_name;
  const isTwilioValid = Boolean(values.sms_gateway_id !== SMS_GATEWAYS.Twilio || (values.twilio_account_sid && values.twilio_api_key && values.twilio_phone_number));
  const isSnoozeTypeValid =
    values.is_snooze_type_next_visit_customer_enabled ||
    values.is_snooze_type_remind_customer_enabled ||
    values.is_snooze_type_make_new_appointment_enabled ||
    values.is_snooze_type_make_offer_enabled;

  const isThresholdsValid = !values.dealer_report_thresholds.some(isThresholdInvalid);

  return !(values.name && isSnoozeTypeValid && isWarrantyValid && isPlanitValid && isTwilioValid && isThresholdsValid);
};

export const DealerOverviewDashboard = () => {
  const { selectedDealer } = useSelectedDealer();
  const { data: thresholds = [] } = useListThresholdsQuery();
  const { mutate: updateDealer, isPending: isUpdateDealerPending } = useUpdateDealerMutation();
  const { mutate: updateThresholds, isPending: isUpdateThresholdsPending } = useUpdateThresholdsMutation();
  const canUpdateThresholds = useCan("update", "dealers-report-threshold");

  if (!selectedDealer) return <Loader active />;

  const onSubmit = (values: DealerOverviewFormPayload) => {
    if (isFormInvalid(values)) return;

    const { dealer_report_thresholds, ...dealerValues } = clearDisabledServiceSecrets(values);
    const enteredThresholds = dealer_report_thresholds.filter(isThresholdEntered);

    if (canUpdateThresholds && enteredThresholds.length) updateThresholds({ dealer_id: selectedDealer.id, dealer_report_thresholds: enteredThresholds });
    updateDealer({ ...selectedDealer, ...dealerValues });
  };

  return (
    <FormHelper.Form onSubmit={onSubmit} defaultValues={getDefaultFormValues(selectedDealer, thresholds)}>
      <div className="DealerOverviewDashboard">
        <DealerOverviewHeader isUpdateDealerPending={isUpdateDealerPending || isUpdateThresholdsPending} />
        <General tabId="general" />
        <DmsSystem tabId="dmsSystem" />
        <Service tabId="service" />
      </div>
    </FormHelper.Form>
  );
};
